import * as EVENTS from '../constants/external-events';
import { AMEX, VISA } from '../constants/partnering-networks';
import {
  OTP_CUSTOM_EVENT_NAME,
  OTP_RESPONSE_EVENT_NAME,
  OTP_SUCCESS_VALIDATION_CHANNEL,
  VSRCI_OTP_RESPONSE_EVENTS
} from '../constants/srci-gtm-events';
import collapsedLogger from '../helpers/collapsed-logger';
import getNetworkInstance from '../helpers/get-network-instance';
import {
  enrichDataLayer,
  getGtmData,
  trackCompleteIdentityValidation,
  trackINOEvent
} from '../helpers/gtm';
import hasSdkError from '../helpers/has-sdk-error';
import OrchCustomData from '../helpers/orch-custom-data';
import sendMessageToVisaSrci from '../helpers/send-message-to-visa-srci';

/*
* SRCi needs to pass the GTM data layer to ORCH layer. This will help to retain the custom attributes in GTM
* Added as part of GTM implementation on initiateIdentityValidation and completeIdentityValidation APIs ref bug #VCO-13577
* data is updated to pass dataLayer from SRCi
*/
const completeIdentityValidation = data => {
  return new Promise(resolve => {
    let vsbGtmData = {};
    const preferredNetwork = OrchCustomData.srcSdks.orchConfig.preferredNetwork;
    const loadedSdk = OrchCustomData.srcSdks.loadedSdk;
    const eventId = EVENTS.ORCH_COMPLETE_IDENTITY_VALIDATION;
    const vco = getNetworkInstance(OrchCustomData, VISA);
    const otpDeliveryChannel = OrchCustomData.srcSdks.orchConfig.otpDeliveryChannel;
    const inputData = enrichDataLayer(data);
    let gtmData = getGtmData(inputData);
    OrchCustomData.apiTimeStamp.completeIdentityValidation = {};
    OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork] = {};
    OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].startTime = Date.now();
    const completeIdentityValidationCache =
      OrchCustomData.srcSdks.apiResponseCache.completeIdentityValidation;
    let identityInput;
    if (preferredNetwork !== AMEX) {
      identityInput = { validationData: data.validationData || data.otpCode || '' };
    } else {
      identityInput = data.validationData || data.otpCode || '';
    }

    if (OrchCustomData.isVsb && !OrchCustomData.isVsbButtonless) {
      vsbGtmData = { isError: false, isRequest: true, isVsb: true };
      trackCompleteIdentityValidation(OrchCustomData, vsbGtmData, preferredNetwork);
    }
    collapsedLogger('completeIdentityValidation -- identityInput =', identityInput);

    return loadedSdk[preferredNetwork]
      .completeIdentityValidation(identityInput)
      .then(response => {
        if (response) {
          collapsedLogger(
            `${preferredNetwork} Network -- completeIdentityValidation response =`,
            response
          );
          OrchCustomData.apiTimeStamp.completeIdentityValidation[
            preferredNetwork
          ].endTime = Date.now();
          OrchCustomData.apiTimeStamp.completeIdentityValidation[
            preferredNetwork
          ].timeTakenInMillSeconds =
            OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].endTime -
            OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].startTime;
          const step = `ORCH_COMPLETE_IDENTITY_VALIDATION_${preferredNetwork}_API_SUCCESS_RESPONSE`;
          if (vco && typeof vco.pushEvents === 'function') {
            vco.pushEvents({
              E: step,
              ERRCODE: null,
              ERRMSG: null,
              ETIME:
                OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].endTime,
              LL: 'INFO',
              S: 200,
              STIME:
                OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].startTime,
              TY: 'RESPONSE',
              checkout_step: step,
              response,
              srcInitiatorId: OrchCustomData.srcInitiatorId[preferredNetwork]
            });
          }
          completeIdentityValidationCache[preferredNetwork] = response;
          OrchCustomData.updateIdToken(response.idToken);
          const customLabel = `${OTP_RESPONSE_EVENT_NAME} for ${otpDeliveryChannel} Successful`;
          VSRCI_OTP_RESPONSE_EVENTS.event = OTP_CUSTOM_EVENT_NAME;
          VSRCI_OTP_RESPONSE_EVENTS.event_action = OTP_RESPONSE_EVENT_NAME;
          VSRCI_OTP_RESPONSE_EVENTS.event_label = customLabel;
          VSRCI_OTP_RESPONSE_EVENTS.otp_provider = preferredNetwork;
          VSRCI_OTP_RESPONSE_EVENTS.otp_delivery_channel = otpDeliveryChannel;
          VSRCI_OTP_RESPONSE_EVENTS.otp_validation_channel =
            response?.[OTP_SUCCESS_VALIDATION_CHANNEL] ?? 'n/a';
          gtmData = Object.assign(gtmData, VSRCI_OTP_RESPONSE_EVENTS);
          trackINOEvent(OTP_CUSTOM_EVENT_NAME, gtmData);
          vsbGtmData = { isError: false, isRequest: false, isVsb: OrchCustomData.isVsb };
          trackCompleteIdentityValidation(OrchCustomData, vsbGtmData, preferredNetwork);
          sendMessageToVisaSrci(eventId, response);
          resolve({ isValidationComplete: true });
        }
      })
      .catch(err => {
        collapsedLogger('catch completeIdentityValidation Error =', err);
        const errorMsg = hasSdkError(err);
        err = { error: { reason: errorMsg } };
        OrchCustomData.apiTimeStamp.completeIdentityValidation[
          preferredNetwork
        ].endTime = Date.now();
        OrchCustomData.apiTimeStamp.completeIdentityValidation[
          preferredNetwork
        ].timeTakenInMillSeconds =
          OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].endTime -
          OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].startTime;
        const stepErr = `ORCH_COMPLETE_IDENTITY_VALIDATION_${preferredNetwork}_API_ERROR_RESPONSE`;
        if (vco && typeof vco.pushEvents === 'function') {
          vco.pushEvents({
            E: stepErr,
            ERRCODE: err && err.error_code ? err.err_code : '',
            ERRMSG: err && err.reason ? err.reason : '',
            ETIME: OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].endTime,
            LL: 'INFO',
            S: null,
            STIME:
              OrchCustomData.apiTimeStamp.completeIdentityValidation[preferredNetwork].startTime,
            TY: 'RESPONSE',
            checkout_step: stepErr,
            response: err,
            srcInitiatorId: OrchCustomData.srcInitiatorId[preferredNetwork]
          });
        }
        const labelForFailedEvent = `${OTP_RESPONSE_EVENT_NAME} for ${otpDeliveryChannel} Failed ${errorMsg}`;
        VSRCI_OTP_RESPONSE_EVENTS.event = OTP_CUSTOM_EVENT_NAME;
        VSRCI_OTP_RESPONSE_EVENTS.event_action = OTP_RESPONSE_EVENT_NAME;
        VSRCI_OTP_RESPONSE_EVENTS.event_label = labelForFailedEvent;
        VSRCI_OTP_RESPONSE_EVENTS.otp_provider = preferredNetwork;
        VSRCI_OTP_RESPONSE_EVENTS.otp_delivery_channel = otpDeliveryChannel;
        gtmData = Object.assign(gtmData, VSRCI_OTP_RESPONSE_EVENTS);
        if (OrchCustomData.isVsb) {
          vsbGtmData = { isError: true, isRequest: false, isVsb: OrchCustomData.isVsb };
          trackCompleteIdentityValidation(OrchCustomData, vsbGtmData, preferredNetwork);
        }
        trackINOEvent(OTP_CUSTOM_EVENT_NAME, gtmData);
        sendMessageToVisaSrci(eventId, err);
        resolve({ error: err.error, isValidationComplete: false });
      })
      .then(() => {
        collapsedLogger('END completeIdentityValidation; OrchCustomData =', OrchCustomData);
      });
  });
};

export default completeIdentityValidation;
